import { FormControlLabel, Radio, Typography } from "@mui/material";

import {
  LicenseCountButton,
  LicenseCountInput,
} from "@app/analytics/account/Billing/components/styles";
import type { NewSubscription } from "@app/analytics/account/Billing/types";
import {
  getAnnualSavings,
  getCadenceAdverb,
  getPlanPriceByPlanAndCadence,
  isPlanDowngrade,
  isSamePlan,
} from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import type { BillingCadence } from "@app/common/types";
import { pluralize } from "@app/common/utils";
import Config from "@app/config";
import { formatCurrency, formatNumber, titleize } from "@app/utils/utils";

import Chip from "@rui/Chip";
import Stack, { Row } from "@rui/Stack";
import { Bold } from "@rui/styles";
import TooltipIcon from "@rui/TooltipIcon";
import TooltipWrapper from "@rui/TooltipWrapper";

import {
  CheckoutButton,
  DetailsHeading,
  PanelContentInnerContainer,
  PanelContentOuterContainer,
  PanelMainContent,
  PanelSection,
  PurchaseOption,
} from "../styles";

const CADENCES: BillingCadence[] = ["annual", "monthly"];

interface BillingSubscriptionCheckoutDetailsProps {
  isLoading: boolean;
  onChangeCadence: (cadence: BillingCadence) => void;
  onChangeLicenses: (update: { delta: -1 | 1 } | { count: number }) => void;
  onContinue: () => void;
  subscription: NewSubscription;
}

/**
 * Step 1 of the subscription checkout process. Includes selecting:
 * - The preferred cadence
 * - Number of licenses
 * */
const BillingSubscriptionCheckoutDetails = ({
  isLoading,
  onChangeCadence,
  onChangeLicenses,
  onContinue,
  subscription,
}: BillingSubscriptionCheckoutDetailsProps) => {
  const {
    currentPlan,
    isEligibleForLegacyPricing,
    licenses,
    scheduledPlan,
    subscription: currentSubscription,
  } = useBillingContext();

  const handleSelectCadence = (cadence: BillingCadence) => () => {
    onChangeCadence(cadence);
  };

  const planLicenseMinimum =
    Config.billing.licenseMinimum[subscription.plan] ?? 1;

  // cannot purchase fewer licenses than there are members in their workspace,
  // or fewer licenses than their selected plan allows
  const minLicenses = Math.max(licenses.numClaimed, planLicenseMinimum);
  const maxLicenses =
    !currentSubscription.inTrialPeriod &&
    isPlanDowngrade(currentPlan, {
      type: subscription.plan,
      billingCadence: subscription.cadence,
    })
      ? currentSubscription.quantity
      : Config.billing.maxSeatAdditions;

  const handleIncrementDecrementLicenseCount = (delta: -1 | 1) => () => {
    onChangeLicenses({ delta });
  };

  const handleChangeLicenseCount: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    const newCount = parseInt(event.target.value);
    onChangeLicenses({
      count: Math.min(
        Math.max(isNaN(newCount) ? 0 : newCount, minLicenses),
        maxLicenses
      ),
    });
  };

  const annualSavings = getAnnualSavings(isEligibleForLegacyPricing);

  const isCurrentPlan = (cadence: BillingCadence) =>
    !currentSubscription.inTrialPeriod &&
    (scheduledPlan
      ? isSamePlan(scheduledPlan, {
          type: subscription.plan,
          billingCadence: cadence,
        })
      : isSamePlan(currentPlan, {
          type: subscription.plan,
          billingCadence: cadence,
        }));

  const singleLicensePrice = getPlanPriceByPlanAndCadence({
    plan: subscription.plan,
    cadence: subscription.cadence,
    useLegacyPrices: isEligibleForLegacyPricing,
    forEntirePeriod: true,
  });

  const rawTotal = singleLicensePrice * subscription.quantity;

  return (
    <PanelContentOuterContainer>
      <PanelContentInnerContainer>
        {/* Shim to keep the main content in the center and the button at the bottom */}
        <Stack />
        <PanelMainContent>
          <PanelSection>
            <DetailsHeading>Billing cycle</DetailsHeading>
            {CADENCES.map((cadence) => {
              const isSelected = subscription.cadence === cadence;
              const isDisabled = isCurrentPlan(cadence);
              const adverb = getCadenceAdverb(cadence);
              return (
                <TooltipWrapper
                  key={cadence}
                  title="You're already on this plan!"
                  show={isDisabled}
                  wrapperSx={{ width: "100%" }}
                  placement="top"
                >
                  <PurchaseOption
                    selected={isSelected}
                    onClick={
                      isDisabled ? undefined : handleSelectCadence(cadence)
                    }
                    disabled={isDisabled}
                  >
                    <Row variant="left">
                      <FormControlLabel
                        disabled={isDisabled}
                        control={<Radio checked={isSelected} />}
                        label={titleize(adverb)}
                      />
                      {cadence === "annual" && (
                        <Chip
                          color="chart.orange.dark"
                          bgcolor="shades.orange.50"
                        >
                          Save {annualSavings}%
                        </Chip>
                      )}
                    </Row>
                    <Stack spacing={0.5}>
                      <Typography
                        sx={{
                          color: isDisabled
                            ? "shades.neutral.500"
                            : "shades.neutral.800",
                        }}
                      >
                        <Bold>
                          {formatCurrency(
                            getPlanPriceByPlanAndCadence({
                              plan: subscription.plan,
                              cadence,
                              useLegacyPrices: isEligibleForLegacyPricing,
                            })
                          )}
                        </Bold>{" "}
                        per license per month
                      </Typography>
                      <Typography
                        variant="bodySecondaryReg"
                        sx={{
                          visibility:
                            cadence === "monthly" ? "hidden" : "visible",
                          color: isDisabled
                            ? "shades.neutral.300"
                            : "shades.neutral.500",
                        }}
                      >
                        (Billed{" "}
                        {formatCurrency(
                          getPlanPriceByPlanAndCadence({
                            cadence,
                            plan: subscription.plan,
                            useLegacyPrices: isEligibleForLegacyPricing,
                            forEntirePeriod: true,
                          }),
                          { digits: 0 }
                        )}{" "}
                        per license {adverb})
                      </Typography>
                    </Stack>
                  </PurchaseOption>
                </TooltipWrapper>
              );
            })}
          </PanelSection>
          <PanelSection>
            <Row variant="left" spacing={1}>
              <DetailsHeading>Number of licenses</DetailsHeading>
              <TooltipIcon
                title="Amount of Read licenses you are purchasing. If purchasing more than one, those licenses will be part of your Workspace."
                placement="bottom-start"
              />
            </Row>
            <Row spacing={2} variant="left">
              <Row>
                <LicenseCountButton
                  kind="decrement"
                  onClick={handleIncrementDecrementLicenseCount(-1)}
                  disabled={subscription.quantity <= minLicenses}
                />
                <LicenseCountInput
                  onChange={handleChangeLicenseCount}
                  value={subscription.quantity}
                  min={minLicenses}
                  max={maxLicenses}
                />
                <LicenseCountButton
                  kind="increment"
                  onClick={handleIncrementDecrementLicenseCount(1)}
                  disabled={subscription.quantity >= maxLicenses}
                />
              </Row>
            </Row>
          </PanelSection>
          <PanelSection>
            <Row
              variant="apart"
              sx={{ color: "shades.neutral.500", width: "100%" }}
            >
              <Typography>
                {formatCurrency(singleLicensePrice, { digits: 0 })} x{" "}
                {formatNumber(subscription.quantity)}{" "}
                {pluralize("license", subscription.quantity)}
              </Typography>
              <Typography>{formatCurrency(rawTotal)}</Typography>
            </Row>
          </PanelSection>
        </PanelMainContent>
        <CheckoutButton
          data-test-id="billing-continue-to-payment"
          onClick={onContinue}
          loading={isLoading}
        >
          Continue to payment info
        </CheckoutButton>
      </PanelContentInnerContainer>
    </PanelContentOuterContainer>
  );
};

export default BillingSubscriptionCheckoutDetails;
